import { useRef, useState, useLayoutEffect, useEffect, useCallback } from 'react';
import MultiSelect, { type MultiSelectProps, type DefaultOptionProps } from '../MultiSelect/MultiSelect';
import Tooltip from '../../Compound/Tooltip/Tooltip';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import { withStyles } from '../../../utils/utils';
import Chip from './Chip/Chip';
import styles from './MultiSelectChip.module.scss';
import chipStyles from './Chip/Chip.module.scss';

const cn = withStyles(styles);
const cnChip = withStyles(chipStyles);

const CHIP_GAP = 6;

export type MultiSelectChipProps<Data extends DefaultOptionProps = DefaultOptionProps> = Omit<
  MultiSelectProps<Data>,
  'renderSelectedValue'
> & {
  onDelete?: (value: string) => void;
};

function MultiSelectChip<Data extends DefaultOptionProps = DefaultOptionProps>({
  options,
  value = [],
  optionValue,
  optionLabel,
  onDelete,
  disabled,
  ...rest
}: MultiSelectChipProps<Data>) {
  const measureRowRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState<number | null>(null);

  function getValue(o: Data) {
    if (optionValue) return optionValue(o);
    return o.value || '';
  }

  function getLabelText(o: Data) {
    if (optionLabel) return optionLabel(o);
    return o.label || '';
  }

  const selectedOptions = (value as string[])
    .map((v) => options.find((o) => getValue(o) === v))
    .filter((o): o is Data => !!o);

  const calculateVisibility = useCallback(() => {
    if (!measureRowRef.current) return;

    const chipEls = Array.from(measureRowRef.current.querySelectorAll<HTMLElement>('[data-measure-chip]'));
    const badgeEl = measureRowRef.current.querySelector<HTMLElement>('[data-measure-badge]');

    if (chipEls.length === 0) {
      setVisibleCount(null);
      return;
    }

    const containerWidth = measureRowRef.current.offsetWidth;

    const badgeWidth = badgeEl?.offsetWidth ?? 0;

    // Step 1: if all chips fit without a badge, no truncation needed
    let totalChipsWidth = 0;
    for (let i = 0; i < chipEls.length; i++) {
      totalChipsWidth += (i > 0 ? CHIP_GAP : 0) + chipEls[i].offsetWidth;
    }
    if (totalChipsWidth <= containerWidth) {
      setVisibleCount(null);
      return;
    }

    // Step 2: chips + badge overflow — find how many chips fit alongside the badge
    const availableForChips = containerWidth - CHIP_GAP - badgeWidth;

    let usedWidth = 0;
    let count = 0;

    for (let i = 0; i < chipEls.length; i++) {
      const chipWidth = chipEls[i].offsetWidth;
      const gapBefore = i > 0 ? CHIP_GAP : 0;

      if (usedWidth + gapBefore + chipWidth <= availableForChips) {
        usedWidth += gapBefore + chipWidth;
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count < chipEls.length ? count : null);
  }, []);

  // Recalculate when the selection changes
  const valueKey = (value as string[]).join(',');
  useLayoutEffect(() => {
    calculateVisibility();
  }, [valueKey]);

  useLayoutEffect(() => {
    calculateVisibility();
  }, [selectedOptions]);

  // Recalculate when the container is resized
  useEffect(() => {
    if (!measureRowRef.current) return;
    const ro = new ResizeObserver(calculateVisibility);
    ro.observe(measureRowRef.current);
    return () => ro.disconnect();
  }, [calculateVisibility]);

  const visibleOptions = visibleCount !== null ? selectedOptions.slice(0, visibleCount) : selectedOptions;
  const hiddenCount = visibleCount !== null ? selectedOptions.length - visibleCount : 0;

  return (
    <div className={cn('wrapper')}>
      <MultiSelect
        {...rest}
        options={options}
        value={value}
        optionValue={optionValue}
        optionLabel={optionLabel}
        disabled={disabled}
        renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
      />

      {selectedOptions.length > 0 && (
        <div className={cn('chipsArea')}>
          {/* Hidden measurement layer: all chips + badge rendered to compute widths */}
          <div ref={measureRowRef} className={cn('measureRow')} aria-hidden="true">
            {selectedOptions.map((opt) => (
              <div key={getValue(opt)} data-measure-chip className={cnChip('chip')}>
                <span className={cnChip('chipLabel')}>{getLabelText(opt)}</span>
                {!disabled && onDelete && (
                  <span className={cnChip('chipRemoveIcon')}>
                    <MaterialSymbol icon="close" size="16px" />
                  </span>
                )}
              </div>
            ))}
            <div data-measure-badge className={cn('moreBadge')}>
              +{selectedOptions.length}
            </div>
          </div>

          {/* Visible display row */}
          <div className={cn('chipsRow')}>
            {visibleOptions.map((opt) => (
              <Chip
                key={getValue(opt)}
                label={getLabelText(opt)}
                disabled={disabled}
                onDelete={onDelete ? () => onDelete(getValue(opt)) : undefined}
              />
            ))}

            {hiddenCount > 0 && (
              <Tooltip
                type="hover"
                hasButton={false}
                position="bottom"
                customElement={<div className={cn('moreBadge')}>+{hiddenCount}</div>}
                information={
                  <div className={cn('tooltipChips', 'scrollbar')}>
                    {selectedOptions.slice(visibleCount!).map((opt) => (
                      <div key={getValue(opt)} className={cn('tooltipChip')}>
                        <span className={cn('tooltipChipLabel')}>{getLabelText(opt)}</span>
                        {!disabled && onDelete && (
                          <button
                            type="button"
                            className={cn('tooltipChipRemove')}
                            onClick={() => onDelete(getValue(opt))}
                          >
                            <MaterialSymbol icon="close" size="14px" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiSelectChip;
